import React from 'react';
import { Package, Loader2 } from 'lucide-react';
import type { ItemCountFilter, OrderCountsBySize } from '../../types/worker';
import { ITEM_COUNT_FILTER_OPTIONS } from '../../types/worker';

interface ItemCountFilterBarProps {
  selectedFilter: ItemCountFilter;
  onFilterChange: (filter: ItemCountFilter) => void;
  counts: OrderCountsBySize;
  loading?: boolean;
}

export const ItemCountFilterBar: React.FC<ItemCountFilterBarProps> = ({
  selectedFilter,
  onFilterChange,
  counts,
  loading = false,
}) => {
  const getCountForFilter = (filter: ItemCountFilter): number => {
    return counts[filter] || 0;
  };

  const getFilterIcon = (filter: ItemCountFilter) => {
    switch (filter) {
      case 'small':
        return (
          <div className="flex -space-x-1">
            <Package className="h-3.5 w-3.5" />
          </div>
        );
      case 'medium':
        return (
          <div className="flex -space-x-1">
            <Package className="h-3.5 w-3.5" />
            <Package className="h-3.5 w-3.5" />
          </div>
        );
      case 'large':
        return (
          <div className="flex -space-x-1">
            <Package className="h-3.5 w-3.5" />
            <Package className="h-3.5 w-3.5" />
            <Package className="h-3.5 w-3.5" />
          </div>
        );
      default:
        return null;
    }
  };

  return (
    <div className="flex flex-col gap-3">
      <div className="flex items-center gap-2">
        <span className="text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
          Filter by Order Size
        </span>
        {loading && (
          <Loader2 className="h-3 w-3 animate-spin text-gray-400" />
        )}
      </div>

      <div className="inline-flex bg-gray-100 dark:bg-gray-700/50 rounded-xl p-1 gap-1">
        {ITEM_COUNT_FILTER_OPTIONS.map((option) => {
          const isActive = selectedFilter === option.value;
          const count = getCountForFilter(option.value);

          return (
            <button
              key={option.value}
              onClick={() => onFilterChange(option.value)}
              disabled={loading}
              className={`
                relative flex items-center gap-2 px-4 py-2.5 rounded-lg text-sm font-medium
                transition-all duration-200 ease-out
                focus:outline-none focus:ring-2 focus:ring-cyan-500 focus:ring-offset-2
                focus:ring-offset-gray-100 dark:focus:ring-offset-gray-700
                disabled:opacity-50 disabled:cursor-not-allowed
                ${isActive
                  ? 'bg-gradient-to-r from-cyan-500 to-teal-600 text-white shadow-lg shadow-cyan-500/25'
                  : 'text-gray-600 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-600/50 hover:text-gray-900 dark:hover:text-white'
                }
              `}
            >
              {option.value !== 'all' && (
                <span className={`hidden sm:flex ${isActive ? 'text-white/80' : 'text-gray-400 dark:text-gray-500'}`}>
                  {getFilterIcon(option.value)}
                </span>
              )}

              <span className="whitespace-nowrap">
                <span className="sm:hidden">{option.shortLabel}</span>
                <span className="hidden sm:inline">{option.label}</span>
              </span>

              <span
                className={`
                  inline-flex items-center justify-center min-w-[1.5rem] h-5 px-1.5
                  text-xs font-semibold rounded-full transition-colors
                  ${isActive
                    ? 'bg-white/20 text-white'
                    : 'bg-gray-200 dark:bg-gray-600 text-gray-600 dark:text-gray-300'
                  }
                `}
              >
                {loading ? (
                  <span className="w-3 h-3 bg-current rounded-full animate-pulse opacity-50" />
                ) : (
                  count.toLocaleString()
                )}
              </span>
            </button>
          );
        })}
      </div>
    </div>
  );
};
